/**
 * NodeInfo detection for ActivityPub instances.
 *
 * Used to detect whether an instance is Mastodon, Pixelfed, or another
 * ActivityPub platform after OAuth login completes.
 *
 * @see https://nodeinfo.diaspora.software/
 */

/** Platform values returned by nodeinfo detection */
export type DetectedPlatform = 'mastodon' | 'pixelfed' | 'activitypub'

interface NodeInfoLink {
    rel: string
    href: string
}

interface WellKnownNodeInfo {
    links: NodeInfoLink[]
}

interface NodeInfo {
    software?: {
        name?: string
    }
}

const NODEINFO_RELS = [
    'http://nodeinfo.diaspora.software/ns/schema/2.1',
    'http://nodeinfo.diaspora.software/ns/schema/2.0',
    'http://nodeinfo.diaspora.software/ns/schema/1.1',
    'http://nodeinfo.diaspora.software/ns/schema/1.0',
]

function normalizeSoftwareName(name: string): DetectedPlatform {
    const lower = name.toLowerCase()
    if (lower === 'pixelfed') return 'pixelfed'
    if (lower === 'mastodon') return 'mastodon'
    return 'activitypub'
}

/**
 * Detect the platform of a Fediverse instance via NodeInfo.
 *
 * Returns 'mastodon', 'pixelfed', or 'activitypub' (generic fallback).
 * Never throws — returns 'activitypub' on any error.
 *
 * @param instanceUrl - e.g. "https://mastodon.social"
 */
export async function detectPlatformFromNodeinfo(instanceUrl: string): Promise<DetectedPlatform> {
    try {
        const base = instanceUrl.replace(/\/$/, '')

        const wellKnownRes = await fetch(`${base}/.well-known/nodeinfo`, {
            signal: AbortSignal.timeout(5000),
        })
        if (!wellKnownRes.ok) return 'activitypub'

        const wellKnown: WellKnownNodeInfo = await wellKnownRes.json()
        const link = wellKnown.links?.find(l => NODEINFO_RELS.includes(l.rel))
        if (!link?.href) return 'activitypub'

        // Validate the nodeinfo URL before following — it comes from a remote server
        // and could otherwise be used for SSRF against internal network addresses
        let nodeInfoUrl: URL
        try {
            nodeInfoUrl = new URL(link.href)
        } catch {
            return 'activitypub'
        }
        if (nodeInfoUrl.protocol !== 'https:' && nodeInfoUrl.protocol !== 'http:') {
            return 'activitypub'
        }

        const nodeInfoRes = await fetch(link.href, {
            signal: AbortSignal.timeout(5000),
        })
        if (!nodeInfoRes.ok) return 'activitypub'

        const nodeInfo: NodeInfo = await nodeInfoRes.json()
        const softwareName = nodeInfo.software?.name
        if (!softwareName) return 'activitypub'

        return normalizeSoftwareName(softwareName)
    } catch {
        return 'activitypub'
    }
}
